import React from 'react';
import {
  ArrowDownLeft,
  ArrowRightLeft,
  ArrowUpRight,
  FileText,
  Landmark,
  Link2,
  Eye,
  User,
  Trash2
} from 'lucide-react';

const TableSkeleton = () => (
  <>
    {[...Array(5)].map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="p-6"><div className="h-10 w-32 rounded-xl bg-slate-100" /></td>
        <td className="p-6"><div className="space-y-2"><div className="h-4 w-48 rounded bg-slate-100" /><div className="h-3 w-24 rounded bg-slate-50" /></div></td>
        <td className="p-6"><div className="h-4 w-24 rounded bg-slate-100" /></td>
        <td className="p-6"><div className="ml-auto h-6 w-20 rounded bg-slate-100" /></td>
        <td className="p-6"><div className="ml-auto h-10 w-24 rounded-xl bg-slate-100" /></td>
      </tr>
    ))}
  </>
);

const canAllocateTransaction = (transaction) => Boolean(
  transaction?.account &&
  transaction?.category !== 'Transfer' &&
  !transaction?.isTransfer &&
  Math.round((Number(transaction.amount || 0) - (transaction.allocations || []).reduce(
    (sum, allocation) => sum + Number(allocation.sourceAmount ?? allocation.amount ?? 0), 0
  )) * 100) > 0
);

const normalizeSearchText = (value) => String(value || '').trim().toLocaleLowerCase('tr-TR');

const matchesTransactionSearch = (transaction, searchTerm) => {
  const term = normalizeSearchText(searchTerm);
  if (!term) return true;

  const searchableFields = [
    transaction?.account?.name,
    transaction?.account?.code,
    transaction?.description,
    transaction?.documentNo
  ];

  return searchableFields
    .map(normalizeSearchText)
    .some((field) => field.includes(term));
};

const TransactionList = ({
  transactions = [],
  loading = false,
  formatCurrency,
  searchTerm = '',
  onRowClick,
  onAllocate,
  canAllocate = true,
  onDelete,
  canDelete = false
}) => (
  <table className="w-full text-left">
    <thead className="bg-slate-50/50">
      <tr>
        <th className="border-b border-slate-100 p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Tarih / Tip</th>
        <th className="border-b border-slate-100 p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Açıklama / İlişki</th>
        <th className="border-b border-slate-100 p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Hesap (Kasa/Banka)</th>
        <th className="border-b border-slate-100 p-6 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Tutar</th>
        <th className="border-b border-slate-100 p-6 text-right text-[10px] font-black uppercase tracking-widest text-slate-400">Aksiyon</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-50">
      {(() => {
        const visibleTransactions = transactions.filter((trx) => matchesTransactionSearch(trx, searchTerm));

        return loading ? (
          <TableSkeleton />
        ) : visibleTransactions.length > 0 ? (
          visibleTransactions.map((trx) => (
            <tr
              key={trx._id}
              onClick={() => onRowClick(trx._id)}
              className="group cursor-pointer transition-colors hover:bg-indigo-50/50"
            >
              <td className="p-6">
                <div className="flex items-center gap-3">
                  <div className={`rounded-xl p-2 ${trx.type === 'Inflow' ? 'bg-emerald-50 text-emerald-600' : trx.type === 'Outflow' ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'}`}>
                    {trx.type === 'Inflow' ? <ArrowUpRight size={18} /> : trx.type === 'Outflow' ? <ArrowDownLeft size={18} /> : <ArrowRightLeft size={18} />}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-black text-slate-900">{new Date(trx.date).toLocaleDateString('tr-TR')}</span>
                    <span className="text-[10px] font-bold uppercase text-slate-600">{trx.category}</span>
                  </div>
                </div>
              </td>
              <td className="p-6">
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-slate-900">{trx.description || 'Açıklama yok'}</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {trx.account ? (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-500">
                        <User size={10} /> {trx.account.name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-500">
                        <Landmark size={10} /> İç Hareket
                      </span>
                    )}
                    {Array.isArray(trx.allocations) && trx.allocations.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">
                        <FileText size={10} /> {trx.allocations.length} fatura
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="p-6 text-sm font-bold text-slate-700">{trx.financialAccount?.name}</td>
              <td className={`p-6 text-right text-base font-semibold ${trx.type === 'Inflow' ? 'text-emerald-700' : trx.type === 'Outflow' ? 'text-rose-700' : 'text-slate-900'}`}>
                {trx.type === 'Outflow' ? '-' : ''}{formatCurrency(trx.amount, trx.financialAccount?.currency)}
              </td>
              <td className="p-6 text-right">
                <div className="inline-flex items-center justify-end gap-1">
                  {canAllocate && canAllocateTransaction(trx) ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onAllocate(trx);
                      }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-30"
                      title="Fatura İlişkilendir"
                      aria-label="Fatura İlişkilendir"
                    >
                      <Link2 size={18} />
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(trx);
                      }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                      title="Hareketi Sil"
                      aria-label="Hareketi Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                  ) : null}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowClick(trx._id);
                      }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                      title="Detay Görüntüle"
                      aria-label="Detay Görüntüle"
                    >
                      <Eye size={18} />
                    </button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="5" className="p-20 text-center">
              <div className="flex flex-col items-center gap-3 opacity-20">
                <FileText size={48} className="text-slate-400" />
                <span className="text-xs font-black uppercase italic tracking-widest">Henüz işlem kaydı bulunamadı</span>
              </div>
            </td>
          </tr>
        );
      })()}
    </tbody>
  </table>
);

export default TransactionList;
